import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';
import { UserProfile, UserRole } from '../../../shared/models/auth.model';
import { AuthService } from '../../../core/services/auth.service';
import { UsersService } from '../../../shared/services/users.service';
import { UserDetailResponse } from '../../../shared/models/users.model';

interface UserTypeOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-users-edit',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    SelectModule,
    CheckboxModule,
    ButtonModule,
  ],
  templateUrl: './users-edit.component.html',
  styleUrl: './users-edit.component.scss',
})
export class UsersEditComponent implements OnInit {

  // User profile for header
  userProfile: UserProfile | null = null;

  // Form
  userForm: FormGroup;
  isSubmitting: boolean = false;
  showPassword: boolean = false;

  // User type options - will be loaded from API
  userTypeOptions: UserTypeOption[] = [];

  // User data to edit
  userData: UserDetailResponse | null = null;
  userId: string | null = null;
  loading: boolean = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private usersService: UsersService
  ) {
    this.userForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      userType: ['', Validators.required],
      isActive: [true],
      isNonActive: [false],
    });
  }

  ngOnInit(): void {
    // Get userId from route query params
    this.route.queryParams.subscribe((params) => {
      if (params['userId']) {
        this.userId = params['userId'];
        this.loadUserData();
      } else {
        // If no userId, redirect back to users list
        this.router.navigate(['/users']);
      }
    });
    this.checkUserRole();
    this.loadUserTypeOptions();
  }

  private loadUserTypeOptions(): void {
    // Load user type options from API
    this.usersService.getActiveRoles().subscribe({
      next: (roles) => {
        this.userTypeOptions = roles;
        // Re-populate the form if user data is already loaded
        if (this.userData) {
          this.populateForm();
        }
      },
      error: () => {
        // Show "No Data Found" if API fails
        this.userTypeOptions = [{ label: 'No Data Found', value: 'no-data' }];
      },
    });
  }

  private loadUserData(): void {
    if (!this.userId) return;
    
    this.loading = true;
    this.usersService.getUserById(this.userId).subscribe({
      next: (user) => {
        this.userData = user;
        this.populateForm();
        this.loading = false;
      },
      error: () => {
        this.router.navigate(['/users']);
        this.loading = false;
      }
    });
  }

  private checkUserRole(): void {
    const currentUser = this.authService.getCurrentUser();

    // Set userProfile from authenticated user
    if (currentUser) {
      this.userProfile = {
        id: currentUser.email,
        name: currentUser.name,
        email: currentUser.email,
        role: currentUser.role,
        avatar: 'assets/images/logo.png',
      };
    }
  }

  private populateForm(): void {
    if (this.userData && this.userTypeOptions.length > 0) {
      // Find the role ID that matches the user's current role
      const userRole = this.userTypeOptions.find(
        (option) => option.label === this.userData?.role
      );
      
      this.userForm.patchValue({
        name: this.userData.fullName || '',
        email: this.userData.email || '',
        userType: userRole ? userRole.value : '',
        isActive: this.userData.isActive,
        isNonActive: !this.userData.isActive,
      });
    }
  }

  // Form validation helpers
  isFieldInvalid(fieldName: string): boolean {
    const field = this.userForm.get(fieldName);
    return field ? field.invalid && (field.dirty || field.touched) : false;
  }

  // Password visibility toggle
  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  // Active status change handler
  onActiveStatusChange(value: boolean): void {
    if (value) {
      // Yes selected - uncheck No
      this.userForm.patchValue({ isNonActive: false });
    } else {
      // No selected - uncheck Yes
      this.userForm.patchValue({ isActive: false });
    }
  }

  // Form submission
  onSubmit(): void {
    if (this.userForm.valid && this.userId) {
      this.isSubmitting = true;

      const formValue = this.userForm.value;
      
      // Find the selected role name from userTypeOptions
      const selectedRole = this.userTypeOptions.find(
        (option) => option.value === formValue.userType
      );
      const roleName = selectedRole ? selectedRole.label : '';

      const updateData = {
        fullName: formValue.name,
        roleName: roleName,
        isActive: formValue.isActive
      };

      this.usersService.editUser(this.userId, updateData).subscribe({
        next: () => {
          this.isSubmitting = false;
          this.router.navigate(['/users']);
        },
        error: () => {
          this.isSubmitting = false;
          // You can add error handling here (show toast, etc.)
        }
      });
    } else {
      // Mark all fields as touched to trigger validation display
      Object.keys(this.userForm.controls).forEach((key) => {
        const control = this.userForm.get(key);
        control?.markAsTouched();
      });
    }
  }

  // Navigation
  goBack(): void {
    this.router.navigate(['/users']);
  }

}
